<template>
  <v-container
    id="subscriptions-view"
    fluid
    tag="section"
  >
    <cumulus-edit-dialog
      ref="editExtensions"
      v-model="dialogExtension"
      icon="$edit"
      :title="$t('order')"
      :loading="loading"
      @save="orderMaintenance"
      @escape="closeDialogExtension"
    >
      <template v-slot:editform>
        <edit-plane-maintenance
          :extension="editedExtension"
          :planes="planes"
        />
      </template>
    </cumulus-edit-dialog>
    <material-card
      icon="$subscriptions"
      icon-small
      color="accent"
      :title="page_title"
      help="subscriptions"
    >
      <template v-slot:title2>
        <div class="text-h5">
          <tooltip-btn
            icon="$back"
            tooltip="planning"
            @click="back"
          />
        </div>
      </template>
      <v-card-title>
        <v-btn
          v-show="false"
          v-shortkey.once="['f2']"
          color="primary"
          class="mb-2 ml-2"
          @shortkey="edit('new')"
          @click="edit('new')"
        >
          {{ $t('order_item',{ item: $t('subscription') }) }}
        </v-btn>
        <v-btn
          v-show="editor"
          color="#E3F2FD"
          class="mb-2 ml-2"
          @click="orderLimit('glider')"
        >
          {{ $t('order_limit',{ item: $t('glider') }) }}
        </v-btn>
        <v-btn
          v-show="editor"
          color="#BBDEFB"
          class="mb-2 ml-2"
          @click="orderLimit('general')"
        >
          {{ $t('order_limit',{ item: $t('general') }) }}
        </v-btn>
        <v-btn
          v-show="editor"
          color="primary"
          class="mb-2 ml-2"
          @click="showDialogExtension('plane_maintenance')"
        >
          {{ $t('order_item',{ item: $t('plane_maintenance') }) }}
        </v-btn>
        <v-spacer />
        <v-switch
          v-model="show_all"
          class="mx-2"
          :label="$t('show_all')"
          hide-details
        />
        <v-text-field
          v-model="search"
          append-icon="$search"
          class="ml-auto"
          hide-details
          :label="$t('search_records')"
          single-line
        />
      </v-card-title>
      <v-card-text>
        <v-data-table
          show-expand
          :headers="computed_headers"
          :items="items"
          :loading="loading"
          :loading-text="$t('loading')"
          :options.sync="options"
          :server-items-length="total"
          :footer-props="footerProps"
          :single-expand="singleExpand"
          :expanded.sync="expanded"
          :item-class="row_classes"
        >
          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="ma-3 pa-6">
              <pre>{{ item.body }}</pre>
            </td>
          </template>
          <template #item.plane="{item}">
            <span v-if="item.plane">{{ item.plane.reg_num }}</span>
            <span v-if="item.payer_organization"> ({{ $t('payer') }}: {{ item.payer_organization.name }})</span>
          </template>
          <template #item.valid_from="{item}">
            {{ item.valid_from | dateFormat }}
          </template>
          <template #item.valid_until="{item}">
            {{ item.valid_until | dateFormat }}
          </template>
          <template #item.actions="{item}">
            <tooltip-btn
              v-for="(action, i) in actions"
              :key="i"
              :show="!action.disable && item.valid_until === null"
              :color="action.color"
              :icon="action.iconCalc ? action.iconCalc(item) : action.icon"
              :tooltip="action.tooltip"
              @click="action.func(item.id)"
            />
          </template>
        </v-data-table>
      </v-card-text>
    </material-card>
  </v-container>
</template>

<script>
  // Utilities
  import { get, call, sync } from 'vuex-pathify'
  import EditPlaneMaintenance from '@/layouts/default/edit/PlaneMaintenance.vue'

  export default {
    name: 'Subscriptions',
    components: {
      EditPlaneMaintenance,
    },
    data () {
      return {
        items: [],
        // table
        footerProps: {
          itemsPerPageOptions: [10, 20, 25, 50, 100],
        },
        total: 0,
        timer: null,
        expanded: [],
        singleExpand: true,
        organization_id: null,
        organization_name: '',
        dialogExtension: false,
        editedExtension: {},
        show_all: false,
        planes: [],
      }
    },
    computed: {
      loading: get('app/loading'),
      profile: get('auth/profile'),
      organizations: get('auth/organizations'),
      options: sync('message/options'),
      search: sync('message/search'),
      headers_data () {
        return [
          {
            hide_small: true,
            text: this.$t('fields.name'),
            align: 'start',
            sortable: true,
            value: 'product.name',
          },
          {
            hide_small: true,
            text: this.$t('fields.comment'),
            sortable: true,
            value: 'plane',
          },
          {
            text: this.$t('fields.valid_from'),
            sortable: true,
            value: 'valid_from',
            width: 200,
          },
          {
            text: this.$t('fields.valid_until'),
            sortable: true,
            value: 'valid_until',
            width: 200,
          },
          {
            text: this.$t('fields.last_billing_at'),
            sortable: true,
            value: 'last_billing_at',
            width: 200,
          },
          {
            text: this.$t('fields.last_billing_number'),
            sortable: true,
            value: 'last_billing_number',
            width: 200,
          },
          {
            text: this.$t('actions'),
            value: 'actions',
            sortable: false,
            align: 'right',
            width: 150,
          },
        ]
      },
      actions () {
        return [
          {
            color: 'red',
            iconCalc: this.getEnabledIcon,
            tooltip: 'tooltip_activate',
            disable: !this.editor,
            func: this.closeOrderClick,
          },
          {
            color: 'edit',
            icon: '$edit',
            tooltip: 'tooltip_edit',
            disable: !this.editor,
            func: this.edit,
          },
        ]
      },
      page_title () { return this.$t('subscriptions') + '- ' + this.organization_name },
      editor () { return true },
      computed_headers () { return this.headers_data.filter(h => h.hide_small !== this.$vuetify.breakpoint.mdAndDown) },
    },
    watch: {
      search: {
        handler () {
          if (this.timer) {
            clearTimeout(this.timer)
            this.timer = null
          }
          this.timer = setTimeout(() => {
            this.load()
          }, 800)
        },
        deep: true,
      },
      show_all: {
        handler () {
          this.load()
        },
        deep: true,
      },
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },
    mounted () {
      this.organization_id = parseInt(this.$route.params.id)
      this.organization_name = Object.values(this.organizations).filter(f => f.id === this.organization_id)[0].name
      this.getOrganizationPlanes({ organization_id: this.organization_id }).then(response => {
        this.planes = response.filter(f => f.maintenance_module_on === 0)
        console.log(response)
      })
    },
    methods: {
      getList: call('subscription/search'),
      getOrganizationPlanes: call('organization/planes'),
      addExtensionMaintenance: call('subscription/addExtensionMaintenance'),
      addExtensionLimit: call('subscription/addExtensionLimit'),
      closeOrder: call('subscription/closeOrder'),
      async load () {
        try {
          const { sortBy, sortDesc, page, itemsPerPage } = this.options
          await this.getList({
            page: page,
            per_page: itemsPerPage,
            sort: {
              field: sortBy,
              order: sortDesc,
            },
            organization_id: this.organization_id,
            search: this.search,
            show_all: this.show_all,
          }).then(response => {
            this.items = response.data
            this.total = response.total
          }).catch(e => {
            console.log(e)
          })
        } catch (e) {
        }
      },
      async closeOrderClick (id) {
        await this.closeOrder(
          { id: id },
        ).then(response => {
          this.load()
        }).catch(e => {
          console.log(e)
        })
      },
      edit (id) {
        let url = ''
        if (id === 'new') {
          url += '/subscription/new'
        } else {
          url += `/subscription/edit/${id}`
        }
        this.$router.push(url).catch(() => {})
      },
      showDialogExtension (type) {
        this.editedExtension = { type: type, organization_id: this.organization_id }
        this.dialogExtension = true
      },
      closeDialogExtension () {
        this.dialogExtension = false
        this.load()
      },
      async orderMaintenance () {
        console.log(this.editedItem)
        this.addExtensionMaintenance(
          this.editedExtension,
        ).then(response => {
          this.editedExtension = {}
          this.closeDialogExtension()
        }).catch(e => {
          this.$refs.editModules.setErrors(e)
        })
      },
      async orderLimit (type) {
        console.log(this.editedItem)
        this.addExtensionLimit(
          {
            organization_id: this.organization_id,
            type: type,
          },
        ).then(response => {
          this.load()
        }).catch(e => {
          console.log(e)
        })
      },
      getEnabledIcon (item) {
        return (item.valid_until !== null) ? 'mdi-check' : 'mdi-close'
      },
      row_classes (item) {
        if (item.valid_until !== null) {
          return 'red lighten-4'
        } else if (item.type === 'P') {
          return 'blue lighten-4'
        } else {
          return 'blue lighten-5'
        }
      },
      back () {
        this.$router.push('/organizations/').catch(() => {})
      },
    },
  }
</script>
